import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { AdminHeader, Badge } from "@/pages/admin/AdminLayout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import MemberDialog from "@/components/admin/MemberDialog";
import { Heart, MapPin, Mail, ArrowRight } from "lucide-react";

const STATUSES = ["new", "reviewing", "contacted", "matchmaking_offered", "date_coordination_started", "closed"];
const tone = { new: "blue", reviewing: "amber", closed: "gray" };

function InterestDialog({ record, onClose, onUpdate, onProfile }) {
    if (!record) return null;
    const r = record;
    return (
        <Dialog open={!!record} onOpenChange={onClose}>
            <DialogContent className="max-w-lg" data-testid="mutual-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Heart className="h-5 w-5 text-accent" /> Mutual Interest</DialogTitle>
                    <DialogDescription>Two members privately expressed interest. Coordinate the introduction from here.</DialogDescription>
                </DialogHeader>
                <div className="flex items-center justify-center gap-3 rounded-xl bg-secondary/50 p-4 text-center">
                    <button type="button" data-testid="mutual-profile-a" onClick={() => r.member_a?.id && onProfile(r.member_a.id)} className="flex-1 rounded-lg p-2 transition-colors hover:bg-accent/10">
                        <p className="font-display text-lg font-700 text-primary underline-offset-2 hover:underline">{r.member_a?.name}</p>
                        <p className="flex items-center justify-center gap-1 text-xs text-muted-foreground"><Mail className="h-3 w-3" /> {r.member_a?.email}</p>
                    </button>
                    <ArrowRight className="h-5 w-5 text-accent" />
                    <button type="button" data-testid="mutual-profile-b" onClick={() => r.member_b?.id && onProfile(r.member_b.id)} className="flex-1 rounded-lg p-2 transition-colors hover:bg-accent/10">
                        <p className="font-display text-lg font-700 text-primary underline-offset-2 hover:underline">{r.member_b?.name}</p>
                        <p className="flex items-center justify-center gap-1 text-xs text-muted-foreground"><Mail className="h-3 w-3" /> {r.member_b?.email}</p>
                    </button>
                </div>
                <div className="space-y-1.5 text-sm">
                    <p className="flex items-center gap-2 text-muted-foreground"><MapPin className="h-4 w-4" /> Met at <span className="font-600 text-foreground">{r.outing_name}</span></p>
                    <p className="text-xs text-muted-foreground">Contact details are never auto-shared. The matchmaker reaches out to both members privately and coordinates from here.</p>
                </div>
                <div className="flex items-center gap-2 pt-1">
                    <span className="text-sm font-600">Status</span>
                    <Select value={r.status} onValueChange={(v) => onUpdate(r.id, v)}>
                        <SelectTrigger data-testid={`mutual-dialog-status-${r.id}`} className="h-9 w-56"><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default function MutualInterest() {
    const qc = useQueryClient();
    const [selected, setSelected] = useState(null);
    const [profileId, setProfileId] = useState(null);
    const { data } = useQuery({ queryKey: ["admin-mutual"], queryFn: async () => (await api.get("/admin/mutual-interests")).data });
    const update = async (id, status) => {
        await api.put(`/admin/mutual-interests/${id}`, { status });
        toast.success("Updated");
        qc.invalidateQueries({ queryKey: ["admin-mutual"] });
        setSelected((s) => (s && s.id === id ? { ...s, status } : s));
    };

    return (
        <div>
            <AdminHeader title="Mutual Interest" subtitle="Two members privately expressed interest in each other. Contact details are never auto-shared — the matchmaker takes it from here." />
            <div className="space-y-3">
                {(data || []).length === 0 && <p className="text-sm text-zinc-500" data-testid="mutual-empty">No mutual interest records yet.</p>}
                {(data || []).map((r) => (
                    <button key={r.id} type="button" data-testid={`mutual-row-${r.id}`} onClick={() => setSelected(r)}
                        className="w-full rounded-lg border border-zinc-200 bg-white p-5 text-left transition-colors hover:border-accent hover:bg-accent/5">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div>
                                <p className="font-600">{r.member_a?.name} <span className="text-zinc-400">↔</span> {r.member_b?.name}</p>
                                <p className="text-sm text-zinc-500">Met at {r.outing_name} · {r.member_a?.email} / {r.member_b?.email}</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <Badge tone={tone[r.status] || "gray"}>{r.status}</Badge>
                                <span className="text-xs font-600 text-accent">Open ›</span>
                            </div>
                        </div>
                    </button>
                ))}
            </div>
            <InterestDialog record={selected} onClose={() => setSelected(null)} onUpdate={update} onProfile={(id) => { setSelected(null); setProfileId(id); }} />
            <MemberDialog userId={profileId} onClose={() => setProfileId(null)} />
        </div>
    );
}
